// src/pages/teacher/TeacherLayout.tsx
// Layout גננת — Mobile-first עם bottom navigation סגול

import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  Wallet,
  ShoppingCart,
  FileText,
  MessageSquare,
  LogOut,
  Settings,
  GraduationCap,
  HelpCircle,
} from 'lucide-react';
import { useEffect } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useSimulationStore } from '../../stores/simulationStore';
import { useHelpGuide } from '../../hooks/useHelpGuide';
import HelpGuideModal from '../../components/common/HelpGuideModal';
import { teacherColors } from '../../styles/colors';

const colors = {
  ...teacherColors,
  background: '#faf5ff',
  bottomNav: '#ffffff',
  bottomNavActive: '#8b5cf6',
  bottomNavInactive: '#9ca3af',
};

const navItems = [
  { path: '/teacher/dashboard', icon: LayoutDashboard, label: 'בית' },
  { path: '/teacher/budgets', icon: Wallet, label: 'תקציבים' },
  { path: '/teacher/purchases', icon: ShoppingCart, label: 'קניות' },
  { path: '/teacher/invoices', icon: FileText, label: 'חשבוניות' },
  { path: '/teacher/messages', icon: MessageSquare, label: 'הודעות' },
];

export default function TeacherLayout() {
  const navigate = useNavigate();
  const { logout, user, isAuthenticated } = useAuthStore();
  const simActive = useSimulationStore(s => s.active);
  const helpGuide = useHelpGuide();

  useEffect(() => {
    document.title = 'KipiKid - גננת';
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', colors.primary);
  }, []);

  // Route guard — teacher requires role 'teacher'
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/teacher/login', { replace: true });
      return;
    }
    if (user && user.role !== 'teacher') {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div dir="rtl" style={{
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingTop: simActive ? '36px' : 0,
    }}>
      {/* Top Header */}
      <header style={{
        position: 'fixed',
        top: simActive ? '36px' : 0,
        left: 0,
        right: 0,
        height: '56px',
        backgroundColor: colors.white,
        borderBottom: `1px solid ${colors.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 16px',
        zIndex: 40,
      }}>
        {/* Left: KG name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <GraduationCap size={20} color="white" />
          </div>
          <div>
            <p style={{ fontSize: '14px', fontWeight: '600', color: colors.text, margin: 0, lineHeight: 1.2 }}>
              {user?.kindergartenName || 'גן ילדים'}
            </p>
            <p style={{ fontSize: '11px', color: colors.textLight, margin: 0 }}>
              {user?.firstName || user?.name || ''}
            </p>
          </div>
        </div>

        {/* Right: Help, Settings & Logout */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={helpGuide.open}
            style={{ padding: '8px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', borderRadius: '8px' }}
            title="מדריך עזרה (Ctrl+/)"
          >
            <HelpCircle size={20} color={colors.textLight} />
          </button>
          <button
            onClick={() => navigate('/teacher/settings')}
            style={{
              padding: '8px',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '8px',
            }}
          >
            <Settings size={20} color={colors.textLight} />
          </button>
          <button
            onClick={handleLogout}
            style={{
              padding: '8px',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '8px',
            }}
          >
            <LogOut size={20} color="#ef4444" />
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main style={{
        paddingTop: `${(simActive ? 36 : 0) + 56 + 16}px`,
        paddingBottom: '80px',
        paddingLeft: '16px',
        paddingRight: '16px',
        minHeight: '100vh',
      }}>
        <Outlet />
      </main>

      <HelpGuideModal isOpen={helpGuide.isOpen} onClose={helpGuide.close} currentPath={helpGuide.currentPath} userRole={helpGuide.userRole} />

      {/* Bottom Navigation */}
      <nav style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: '64px',
        backgroundColor: colors.bottomNav,
        borderTop: `1px solid ${colors.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        zIndex: 40,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}>
        {navItems.map(item => (
          <NavLink
            key={item.path}
            to={item.path}
            style={({ isActive }) => ({
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '2px',
              padding: '6px 12px',
              textDecoration: 'none',
              color: isActive ? colors.bottomNavActive : colors.bottomNavInactive,
              transition: 'color 0.2s',
              position: 'relative',
            })}
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <motion.div
                    layoutId="teacher-nav-indicator"
                    style={{
                      position: 'absolute',
                      top: '-1px',
                      width: '24px',
                      height: '3px',
                      backgroundColor: colors.primary,
                      borderRadius: '0 0 3px 3px',
                    }}
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                )}
                <item.icon size={22} />
                <span style={{ fontSize: '10px', fontWeight: isActive ? '600' : '400' }}>
                  {item.label}
                </span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
